import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Upload, Paperclip, X, Send, AlertCircle, Image as ImageIcon, FileText,
  Plus, Trash2, MessageCircle, Tag as TagIcon, Edit2, Sparkles,
  Lightbulb, Layers, Pencil, Wrench, CheckCircle2, HelpCircle,
} from "lucide-react";

const KINDS = [
  { v: "idea",      label: "Idea",      icon: Lightbulb, accent: "#8B5FBF" },
  { v: "concept",   label: "Concept",   icon: Layers,    accent: "#A878D4" },
  { v: "design",    label: "Design",    icon: Pencil,    accent: "#6B4A8E" },
  { v: "prototype", label: "Prototype", icon: Wrench,    accent: "#C9A14A" },
  { v: "decision",  label: "Decision",  icon: CheckCircle2, accent: "#5E8B5F" },
  { v: "question",  label: "Question",  icon: HelpCircle, accent: "#B8694A" },
];

const STATUS_OPTIONS = [
  { v: "open",        label: "Open" },
  { v: "shortlisted", label: "Shortlisted" },
  { v: "in_progress", label: "In progress" },
  { v: "done",        label: "Done" },
  { v: "parked",      label: "Parked" },
];

const STATUS_PILL = {
  open:        "bg-white border border-[var(--bloom-ink-soft)] text-[var(--bloom-ink-soft)]",
  shortlisted: "bg-[#F4E3CD] text-[#7A5232] border border-[#E1C49F]",
  in_progress: "bg-[#C9A14A] text-white",
  done:        "bg-[var(--bloom-brand)] text-white",
  parked:      "bg-[var(--bloom-surface)] text-[var(--bloom-ink-muted)] border border-[var(--bloom-border)]",
};

const ACCEPT_FILES = "image/*,application/pdf,application/zip,.dwg,.dxf,.step,.stp,.stl,.obj,.3mf,.docx,.xlsx,.pptx,.txt";
const MAX_FILE_MB = 12;

function bytesLabel(n) {
  if (!n) return "";
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / (1024 * 1024)).toFixed(1)}MB`;
}

function isImageMime(m) { return (m || "").startsWith("image/"); }

function kindMeta(k) { return KINDS.find((x) => x.v === k) || KINDS[0]; }

async function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function filesToAttachments(fileList) {
  const out = [];
  const errors = [];
  for (const f of fileList) {
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      errors.push(`${f.name}: too large (${(f.size / 1024 / 1024).toFixed(1)}MB > ${MAX_FILE_MB}MB)`);
      continue;
    }
    const data_url = await readAsDataURL(f);
    out.push({
      name: f.name,
      mime: f.type || "application/octet-stream",
      data_url,
    });
  }
  return { attachments: out, errors };
}

export default function WateringBlockLabPanel() {
  const [posts, setPosts] = useState([]);
  const [rollup, setRollup] = useState({});
  const [kindFilter, setKindFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showComposer, setShowComposer] = useState(false);
  const [openPostId, setOpenPostId] = useState(null);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (kindFilter) params.set("kind", kindFilter);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/admin/etchery/lab/posts?${params.toString()}`);
      setPosts(data.posts || []);
      setRollup(data.rollup || {});
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kindFilter, statusFilter]);

  const openPost = useMemo(() => posts.find((p) => p.id === openPostId), [posts, openPostId]);

  return (
    <div data-testid="watering-block-lab">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-1">The Etchery × Bloom Club</div>
        <h2 className="font-serif text-4xl md:text-5xl leading-none">Watering Block Lab</h2>
        <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] mt-3 max-w-2xl">
          The team workspace. Drop ideas, share concepts, upload designs and prototypes, and decide together. Everything lives here, nothing gets lost.
        </p>
      </div>

      {/* Rollup tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="lab-rollup">
        <Tile label="Posts" value={rollup.total || 0} accent />
        <Tile label="Open" value={rollup.open || 0} />
        <Tile label="In progress" value={rollup.in_progress || 0} />
        <Tile label="Files" value={rollup.attachments || 0} />
        <Tile label="Comments" value={rollup.comments || 0} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="bloom-input max-w-[170px]" data-testid="lab-kind-filter">
          <option value="">All kinds</option>
          {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bloom-input max-w-[170px]" data-testid="lab-status-filter">
          <option value="">Any status</option>
          {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <button onClick={() => setShowComposer(true)} className="bloom-btn bloom-btn-primary ml-auto" data-testid="lab-new-post-btn">
          <Plus size={14} /> Drop an idea
        </button>
      </div>

      {err && (
        <div className="mb-5 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="lab-error">
          <AlertCircle size={14} /> {err}
        </div>
      )}

      {/* Posts feed */}
      {loading ? (
        <Loading />
      ) : posts.length === 0 ? (
        <EmptyState onCreate={() => setShowComposer(true)} />
      ) : (
        <div className="space-y-5" data-testid="lab-posts">
          {posts.map((p) => (
            <PostCard key={p.id} post={p} onOpen={() => setOpenPostId(p.id)} onChanged={load} />
          ))}
        </div>
      )}

      {showComposer && (
        <ComposerModal
          onClose={() => setShowComposer(false)}
          onCreated={() => { setShowComposer(false); load(); }}
        />
      )}

      {openPost && (
        <PostDetailModal
          post={openPost}
          onClose={() => setOpenPostId(null)}
          onChanged={(updated) => {
            setPosts((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
          }}
          onDeleted={() => { setOpenPostId(null); load(); }}
        />
      )}
    </div>
  );
}

/* ============================ Tile ================================ */
function Tile({ label, value, accent }) {
  return (
    <div className="bloom-card p-4">
      <div className="bloom-label mb-2">{label}</div>
      <div className={`font-serif text-3xl ${accent ? "text-[var(--bloom-brand)]" : ""}`}>{value}</div>
    </div>
  );
}

function Loading() { return <div className="bloom-card p-14 text-center font-serif italic text-[var(--bloom-ink-muted)]">Loading the lab…</div>; }

function EmptyState({ onCreate }) {
  return (
    <div className="bloom-card p-14 text-center" data-testid="lab-empty">
      <Sparkles size={28} strokeWidth={1.3} className="mx-auto mb-4 text-[var(--bloom-accent)]" />
      <h3 className="font-serif text-2xl mb-2">No ideas yet, what should we build first?</h3>
      <p className="font-serif italic text-[var(--bloom-ink-muted)] mb-6 max-w-md mx-auto">
        Drop a sketch, share a concept, attach a CAD file or just pose a question. Everything starts somewhere.
      </p>
      <button onClick={onCreate} className="bloom-btn bloom-btn-primary" data-testid="lab-empty-cta">
        <Plus size={14} /> Drop the first idea
      </button>
    </div>
  );
}

/* ============================ Post card ============================ */
function PostCard({ post, onOpen, onChanged }) {
  const k = kindMeta(post.kind);
  const Icon = k.icon;
  const imgs = (post.attachments || []).filter((a) => isImageMime(a.mime)).slice(0, 4);
  const otherCount = (post.attachments || []).filter((a) => !isImageMime(a.mime)).length;

  return (
    <article className="bloom-card p-5 hover:border-[var(--bloom-ink-soft)] transition cursor-pointer" onClick={onOpen} data-testid={`lab-post-${post.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 shrink-0 flex items-center justify-center border" style={{ borderColor: k.accent, background: `${k.accent}1A` }}>
          <Icon size={18} strokeWidth={1.5} style={{ color: k.accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mb-1">
                <span>{k.label}</span>
                <span>·</span>
                <span>{post.author?.name}</span>
                <span>·</span>
                <span>{new Date(post.created_at).toLocaleDateString()}</span>
              </div>
              <h3 className="font-serif text-xl leading-tight" data-testid={`lab-post-title-${post.id}`}>{post.title}</h3>
            </div>
            <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-1 shrink-0 ${STATUS_PILL[post.status] || STATUS_PILL.open}`}>
              {STATUS_OPTIONS.find((s) => s.v === post.status)?.label || post.status}
            </span>
          </div>

          {post.body && (
            <p className="font-serif text-sm text-[var(--bloom-ink-soft)] whitespace-pre-line line-clamp-3 mt-2">{post.body}</p>
          )}

          {imgs.length > 0 && (
            <div className="mt-4 grid grid-cols-4 gap-2" data-testid={`lab-post-images-${post.id}`}>
              {imgs.map((a) => (
                <div key={a.id} className="aspect-square overflow-hidden bg-[var(--bloom-surface)] border border-[var(--bloom-border)]">
                  <img src={a.data_url} alt={a.name} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-4 mt-4 text-xs text-[var(--bloom-ink-muted)]">
            {otherCount > 0 && (
              <span className="inline-flex items-center gap-1" data-testid={`lab-post-files-${post.id}`}>
                <Paperclip size={12} /> {otherCount} file{otherCount !== 1 ? "s" : ""}
              </span>
            )}
            {(post.comments || []).length > 0 && (
              <span className="inline-flex items-center gap-1">
                <MessageCircle size={12} /> {post.comments.length} comment{post.comments.length !== 1 ? "s" : ""}
              </span>
            )}
            {(post.tags || []).length > 0 && (
              <span className="inline-flex items-center gap-1">
                <TagIcon size={12} /> {post.tags.join(", ")}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

/* ============================ Composer ============================= */
function ComposerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ kind: "idea", title: "", body: "", tags: "" });
  const [files, setFiles] = useState([]); // local File[]
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const { errors } = await filesToAttachments(picked);
    if (errors.length) setErr(errors.join("; "));
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = ""; // allow re-picking same file
  };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setErr("Give it a title so we can find it later.");
    setBusy(true); setErr("");
    try {
      const { attachments, errors } = await filesToAttachments(files);
      if (errors.length) { setBusy(false); return setErr(errors.join("; ")); }
      const payload = {
        kind: form.kind,
        title: form.title.trim(),
        body: form.body || "",
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        attachments,
      };
      await api.post("/admin/etchery/lab/posts", payload);
      onCreated?.();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="lab-composer">
      <div className="bg-[var(--bloom-bg)] w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bloom-border)]">
          <h2 className="font-serif text-2xl">Drop an idea</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]" data-testid="lab-composer-close"><X size={16} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="lab-kind-picker">
            {KINDS.map((k) => {
              const Icon = k.icon;
              const active = form.kind === k.v;
              return (
                <button
                  key={k.v}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, kind: k.v }))}
                  className={`flex items-center gap-2 px-3 py-2.5 border transition ${active ? "border-[var(--bloom-ink)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)] hover:border-[var(--bloom-ink-soft)]"}`}
                  style={active ? { borderColor: k.accent } : {}}
                  data-testid={`lab-kind-${k.v}`}
                >
                  <Icon size={14} strokeWidth={1.5} style={{ color: k.accent }} />
                  <span className="font-serif text-sm">{k.label}</span>
                </button>
              );
            })}
          </div>

          <div>
            <div className="bloom-label mb-2">Title *</div>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bloom-input"
              placeholder="e.g. Try sphagnum core in v3 prototype"
              autoFocus
              data-testid="lab-title-input"
            />
          </div>

          <div>
            <div className="bloom-label mb-2">Body</div>
            <textarea
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="bloom-input"
              placeholder="What are you thinking? Any context, measurements, links."
              data-testid="lab-body-input"
            />
          </div>

          <div>
            <div className="bloom-label mb-2">Tags (comma separated, optional)</div>
            <input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="bloom-input"
              placeholder="watering-block, v3, prototype"
              data-testid="lab-tags-input"
            />
          </div>

          {/* File uploader */}
          <div>
            <div className="bloom-label mb-2">Attach files (images, PDFs, CAD, docs)</div>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="w-full border-2 border-dashed border-[var(--bloom-border)] hover:border-[var(--bloom-ink-soft)] bg-[var(--bloom-surface)]/40 py-6 px-4 flex flex-col items-center gap-2 transition"
              data-testid="lab-upload-btn"
            >
              <Upload size={20} strokeWidth={1.4} className="text-[var(--bloom-ink-soft)]" />
              <div className="font-serif text-sm">Click to upload</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-[var(--bloom-ink-muted)]">
                Images, PDF, ZIP, CAD (.stl .step .dwg), docs · up to {MAX_FILE_MB}MB each
              </div>
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT_FILES}
              onChange={onPick}
              className="hidden"
              data-testid="lab-file-input"
            />

            {files.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="lab-file-staging">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 border border-[var(--bloom-border)] bg-white/50">
                    {f.type.startsWith("image/") ? <ImageIcon size={14} className="text-[var(--bloom-brand)]" /> : <FileText size={14} className="text-[var(--bloom-ink-soft)]" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-serif truncate">{f.name}</div>
                      <div className="text-[10px] text-[var(--bloom-ink-muted)]">{bytesLabel(f.size)}</div>
                    </div>
                    <button type="button" onClick={() => removeFile(i)} className="text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-accent)]" data-testid={`lab-file-remove-${i}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {err && (
            <div className="flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="lab-composer-error">
              <AlertCircle size={14} /> {err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="bloom-btn bloom-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="lab-submit">
              <Send size={13} /> {busy ? "Posting…" : "Post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================ Detail modal ========================== */
function PostDetailModal({ post, onClose, onChanged, onDeleted }) {
  const [current, setCurrent] = useState(post);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ kind: post.kind, title: post.title, body: post.body, tags: (post.tags || []).join(", ") });
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  const extraRef = useRef(null);
  const k = kindMeta(current.kind);
  const Icon = k.icon;

  useEffect(() => { setCurrent(post); }, [post]);

  const updateState = (p) => { setCurrent(p); onChanged?.(p); };

  const changeStatus = async (s) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/etchery/lab/posts/${current.id}/status`, { status: s });
      updateState(data.post);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch(`/admin/etchery/lab/posts/${current.id}`, {
        kind: editForm.kind,
        title: editForm.title.trim(),
        body: editForm.body || "",
        tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      updateState(data.post);
      setEditing(false);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      const { data } = await api.delete(`/admin/etchery/lab/posts/${current.id}/attachments/${attId}`);
      updateState(data.post);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  const onPickMoreFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setBusy(true); setErr("");
    try {
      const { attachments, errors } = await filesToAttachments(picked);
      if (errors.length) throw new Error(errors.join("; "));
      const { data } = await api.post(`/admin/etchery/lab/posts/${current.id}/attachments`, attachments);
      updateState(data.post);
    } catch (e2) {
      setErr(e2.response?.data?.detail ? formatApiError(e2.response.data.detail) : e2.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const addComment = async () => {
    if (!comment.trim() && files.length === 0) return;
    setBusy(true); setErr("");
    try {
      const { attachments, errors } = await filesToAttachments(files);
      if (errors.length) throw new Error(errors.join("; "));
      const { data } = await api.post(`/admin/etchery/lab/posts/${current.id}/comments`, {
        body: comment.trim() || "(file attachment)",
        attachments,
      });
      updateState(data.post);
      setComment(""); setFiles([]);
    } catch (e) {
      setErr(e.response?.data?.detail ? formatApiError(e.response.data.detail) : e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      const { data } = await api.delete(`/admin/etchery/lab/posts/${current.id}/comments/${cid}`);
      updateState(data.post);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post and all its files + comments? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/etchery/lab/posts/${current.id}`);
      onDeleted?.();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="lab-detail-modal">
      <div className="bg-[var(--bloom-bg)] w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[var(--bloom-border)]">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 shrink-0 flex items-center justify-center border" style={{ borderColor: k.accent, background: `${k.accent}1A` }}>
              <Icon size={18} strokeWidth={1.5} style={{ color: k.accent }} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mb-1">
                {k.label} · {current.author?.name} · {new Date(current.created_at).toLocaleDateString()}
              </div>
              {editing ? (
                <input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="bloom-input font-serif text-2xl"
                  data-testid="lab-edit-title"
                />
              ) : (
                <h2 className="font-serif text-2xl leading-tight">{current.title}</h2>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!editing && (
              <button onClick={() => setEditing(true)} className="p-1.5 hover:bg-[var(--bloom-surface)]" data-testid="lab-edit-btn"><Edit2 size={14} /></button>
            )}
            <button onClick={deletePost} className="p-1.5 hover:bg-[var(--bloom-surface)] text-[var(--bloom-accent)]" data-testid="lab-delete-btn"><Trash2 size={14} /></button>
            <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]" data-testid="lab-detail-close"><X size={16} /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Status + kind controls */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">Status:</span>
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.v}
                onClick={() => changeStatus(s.v)}
                disabled={busy}
                className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 transition ${current.status === s.v ? STATUS_PILL[s.v] : "bg-white border border-[var(--bloom-border)] text-[var(--bloom-ink-muted)] hover:border-[var(--bloom-ink-soft)]"}`}
                data-testid={`lab-status-${s.v}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {editing && (
            <div className="bloom-card p-4 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {KINDS.map((kk) => {
                  const IconK = kk.icon;
                  const active = editForm.kind === kk.v;
                  return (
                    <button key={kk.v} type="button" onClick={() => setEditForm({ ...editForm, kind: kk.v })}
                      className={`flex items-center gap-2 px-3 py-2 border text-sm ${active ? "border-[var(--bloom-ink)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)]"}`}>
                      <IconK size={13} style={{ color: kk.accent }} /> {kk.label}
                    </button>
                  );
                })}
              </div>
              <textarea
                rows={4}
                value={editForm.body}
                onChange={(e) => setEditForm({ ...editForm, body: e.target.value })}
                className="bloom-input"
                data-testid="lab-edit-body"
              />
              <input
                value={editForm.tags}
                onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                className="bloom-input"
                placeholder="comma, separated, tags"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditing(false)} className="bloom-btn bloom-btn-ghost">Cancel</button>
                <button onClick={saveEdit} disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="lab-edit-save">Save</button>
              </div>
            </div>
          )}

          {!editing && current.body && (
            <p className="font-serif text-base whitespace-pre-line leading-relaxed text-[var(--bloom-ink)]">{current.body}</p>
          )}

          {(current.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {current.tags.map((t) => (
                <span key={t} className="text-[10px] tracking-[0.15em] uppercase border border-[var(--bloom-border)] px-2 py-1 text-[var(--bloom-ink-soft)]">{t}</span>
              ))}
            </div>
          )}

          {/* Attachments grid */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="bloom-label">Files ({(current.attachments || []).length})</div>
              <button
                type="button"
                onClick={() => extraRef.current?.click()}
                className="bloom-btn bloom-btn-ghost text-xs"
                data-testid="lab-add-files-btn"
              >
                <Upload size={12} /> Add files
              </button>
              <input ref={extraRef} type="file" multiple accept={ACCEPT_FILES} onChange={onPickMoreFiles} className="hidden" />
            </div>
            {(current.attachments || []).length === 0 ? (
              <div className="text-sm font-serif italic text-[var(--bloom-ink-muted)]">No files attached yet.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="lab-attachments">
                {current.attachments.map((a) => (
                  <AttachmentTile key={a.id} att={a} onDelete={() => removeAttachment(a.id)} />
                ))}
              </div>
            )}
          </div>

          {/* Comments */}
          <div>
            <div className="bloom-label mb-3">Comments ({(current.comments || []).length})</div>
            <div className="space-y-3">
              {(current.comments || []).map((c) => (
                <div key={c.id} className="bloom-card p-4" data-testid={`lab-comment-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
                      {c.author?.name} · {new Date(c.created_at).toLocaleString()}
                    </div>
                    <button onClick={() => removeComment(c.id)} className="text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-accent)]"><Trash2 size={12} /></button>
                  </div>
                  <p className="font-serif text-sm whitespace-pre-line text-[var(--bloom-ink)]">{c.body}</p>
                  {(c.attachments || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {c.attachments.map((a) => <AttachmentTile key={a.id} att={a} compact />)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add comment */}
            <div className="mt-4 bloom-card p-4 space-y-3">
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="bloom-input"
                placeholder="Add a comment, paste a link, attach a file…"
                data-testid="lab-comment-input"
              />
              {files.length > 0 && (
                <div className="space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <Paperclip size={11} className="text-[var(--bloom-ink-muted)]" />
                      <span className="font-serif truncate flex-1">{f.name}</span>
                      <span className="text-[var(--bloom-ink-muted)]">{bytesLabel(f.size)}</span>
                      <button onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))} className="text-[var(--bloom-ink-muted)]"><X size={11} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="bloom-btn bloom-btn-ghost text-xs"
                  data-testid="lab-comment-attach-btn"
                >
                  <Paperclip size={12} /> Attach
                </button>
                <input ref={inputRef} type="file" multiple accept={ACCEPT_FILES}
                  onChange={(e) => { const arr = Array.from(e.target.files || []); setFiles((p) => [...p, ...arr]); e.target.value = ""; }}
                  className="hidden" />
                <button onClick={addComment} disabled={busy || (!comment.trim() && files.length === 0)} className="bloom-btn bloom-btn-primary text-sm" data-testid="lab-comment-submit">
                  <Send size={12} /> Post comment
                </button>
              </div>
            </div>
          </div>

          {err && (
            <div className="flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic">
              <AlertCircle size={14} /> {err}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AttachmentTile({ att, compact = false, onDelete }) {
  const isImage = isImageMime(att.mime);
  const sizeText = bytesLabel(att.size_bytes);
  return (
    <div className="relative group border border-[var(--bloom-border)] bg-white/60" data-testid={`lab-att-${att.id}`}>
      {isImage ? (
        <a href={att.data_url} target="_blank" rel="noreferrer" className="block">
          <div className={compact ? "aspect-square" : "aspect-[4/3]"}>
            <img src={att.data_url} alt={att.name} className="w-full h-full object-cover" />
          </div>
        </a>
      ) : (
        <a href={att.data_url} download={att.name} className="block p-4 flex flex-col items-center text-center">
          <FileText size={compact ? 22 : 30} strokeWidth={1.4} className="text-[var(--bloom-ink-soft)] mb-2" />
          <div className="text-[10px] tracking-[0.15em] uppercase text-[var(--bloom-ink-muted)] mb-1">
            {(att.mime || "file").split("/")[1]?.toUpperCase() || "FILE"}
          </div>
        </a>
      )}
      <div className="px-2 py-1.5 border-t border-[var(--bloom-border)] flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-serif truncate">{att.name}</div>
          {sizeText && <div className="text-[10px] text-[var(--bloom-ink-muted)]">{sizeText}</div>}
        </div>
        {onDelete && (
          <button onClick={onDelete} className="text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-accent)] shrink-0" aria-label="Remove">
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
